import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Shield, Users, X, CheckSquare, RotateCcw } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '', permission_keys: [] });
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const [rolesResponse, catalogResponse] = await Promise.all([
        api.get('/settings/roles/'),
        api.get('/settings/roles/permission-catalog/'),
      ]);
      setRoles(rolesResponse.data);
      setPermissionCatalog(catalogResponse.data || []);
    } catch (error) {
      toast.error('Ошибка загрузки ролей');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/settings/roles/${editingId}/`, formData);
        toast.success('Роль обновлена');
      } else {
        await api.post('/settings/roles/', formData);
        toast.success('Роль создана');
      }
      closeModal();
      fetchRoles();
    } catch (error) {
      toast.error('Ошибка сохранения роли');
    }
  };

  const handleEdit = (role) => {
    setFormData({
      name: role.name,
      description: role.description || '',
      permission_keys: role.permission_keys ?? role.effective_permission_keys ?? [],
    });
    setEditingId(role.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить роль?')) {
      try {
        await api.delete(`/settings/roles/${id}/`);
        toast.success('Роль удалена');
        fetchRoles();
      } catch (error) {
        toast.error('Ошибка удаления роли');
      }
    }
  };

  const openAddModal = () => {
    setFormData({ name: '', description: '', permission_keys: [] });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ name: '', description: '', permission_keys: [] });
    setEditingId(null);
  };

  const getRoleIcon = (roleName) => {
    const name = roleName.toLowerCase();
    if (name.includes('admin')) return '👑';
    if (name.includes('manager')) return '👨‍💼';
    if (name.includes('engineer')) return '🔧';
    if (name.includes('operator')) return '👨‍💻';
    return '👤';
  };

  const groupedPermissions = permissionCatalog.reduce((groups, permission) => {
    const group = permission.group || 'Прочее';
    if (!groups[group]) groups[group] = [];
    groups[group].push(permission);
    return groups;
  }, {});

  const togglePermission = (permissionKey) => {
    setFormData((current) => {
      const selected = new Set(current.permission_keys || []);
      if (selected.has(permissionKey)) selected.delete(permissionKey);
      else selected.add(permissionKey);
      return { ...current, permission_keys: Array.from(selected) };
    });
  };

  const togglePermissionGroup = (permissions) => {
    setFormData((current) => {
      const selected = new Set(current.permission_keys || []);
      const keys = permissions.map((permission) => permission.key);
      const allSelected = keys.every((key) => selected.has(key));
      keys.forEach((key) => allSelected ? selected.delete(key) : selected.add(key));
      return { ...current, permission_keys: Array.from(selected) };
    });
  };

  const restoreCompatibleDefaults = () => {
    const role = roles.find((item) => item.id === editingId);
    setFormData((current) => ({
      ...current,
      permission_keys: role?.effective_permission_keys || [],
    }));
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка ролей...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <Shield size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Роли пользователей
        </h3>
        <button className={styles.addButton} onClick={openAddModal}>
          <Plus size={18} />
          Добавить роль
        </button>
      </div>

      <div className={styles.rolePermissionsIntro}>
        <Shield size={20} />
        <div>
          <strong>Где настраиваются полномочия</strong>
          <p>Откройте нужную роль кнопкой «Настроить права». Галочки управляют и видимостью разделов, и серверным разрешением действий. Метка «совместимые» означает прежний набор прав; после сохранения он станет явной настройкой роли.</p>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        {roles.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <Shield size={48} />
            </div>
            <h3>Нет ролей</h3>
            <p>Добавьте первую роль для управления доступом пользователей</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Описание</th>
                <th>Разрешения</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id} className={styles.clickableRow} onClick={() => handleEdit(role)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.25rem' }}>{getRoleIcon(role.name)}</span>
                      <div>
                        <strong>{role.name}</strong>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginTop: '0.125rem'
                        }}>
                          ID: {role.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {role.description ? (
                      <div style={{ 
                        maxWidth: '300px',
                        lineHeight: '1.4',
                        color: '#475569'
                      }}>
                        {role.description}
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                        Описание не указано
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={styles.permissionCount}>
                      {(role.effective_permission_keys || []).length}
                    </span>
                    {role.uses_legacy_defaults && (
                      <span className={styles.permissionDefaults}>совместимые</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.roleActions}>
                      <button
                        className={styles.permissionEditButton}
                        onClick={(event) => { event.stopPropagation(); handleEdit(role); }}
                        title="Настроить права"
                      >
                        <CheckSquare size={16} /> Настроить права
                      </button>
                      <button
                        className={styles.actionButton}
                        onClick={(event) => { event.stopPropagation(); handleDelete(role.id); }}
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <Shield size={20} />
                {editingId ? 'Редактировать роль' : 'Добавить роль'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>
                    <Shield size={16} />
                    Название роли
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Введите название роли"
                    required
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <Users size={16} />
                    Описание роли
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Введите описание роли и её полномочий"
                    rows={3}
                  />
                </div>

                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <div className={styles.permissionHeader}>
                    <label>
                      <CheckSquare size={16} />
                      Возможности роли
                    </label>
                    {editingId && (
                      <button
                        type="button"
                        className={styles.permissionResetButton}
                        onClick={restoreCompatibleDefaults}
                      >
                        <RotateCcw size={15} />
                        Текущие совместимые права
                      </button>
                    )}
                  </div>
                  <p className={styles.permissionHint}>
                    Эти права проверяются сервером. Снятая галочка не только скрывает раздел,
                    но и запрещает соответствующее действие через API.
                  </p>
                  <div className={styles.permissionGroups}>
                    {Object.entries(groupedPermissions).map(([groupName, permissions]) => {
                      const selected = new Set(formData.permission_keys || []);
                      const selectedCount = permissions.filter((permission) => selected.has(permission.key)).length;
                      return (
                        <section className={styles.permissionGroup} key={groupName}>
                          <button
                            type="button"
                            className={styles.permissionGroupTitle}
                            onClick={() => togglePermissionGroup(permissions)}
                          >
                            <span>{groupName}</span>
                            <span>{selectedCount} / {permissions.length}</span>
                          </button>
                          <div className={styles.permissionGrid}>
                            {permissions.map((permission) => (
                              <label className={styles.permissionOption} key={permission.key}>
                                <input
                                  type="checkbox"
                                  checked={selected.has(permission.key)}
                                  onChange={() => togglePermission(permission.key)}
                                />
                                <span>
                                  <strong>{permission.label}</strong>
                                  <small>{permission.description}</small>
                                </span>
                              </label>
                            ))}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                </div>
                
                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Обновить роль' : 'Создать роль'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleList;
